import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import Cookies from "js-cookie";

// Context & Services
import AppContext from "../../context/AppContext";
import { getCallCenterApiErrorMessage } from "../../service/CallCenterApi";
import {
  createCcWorkspaceUser,
  fetchRolesForUserAssignment,
  getCcWorkspaceUser,
  listCcSupervisorOptions,
  patchCcWorkspaceUser,
  resetCcWorkspaceUserPassword,
} from "../../service/CallCenterUserAdmin";
import {
  ORG_CONTEXT_CHANGED_EVENT,
  ensureSuperAdminOrganizationContext,
} from "../../utils/organizationContext";

// Components
import translations from "../../components/common/Translations";
import ReactSelector from "../../components/common/CommonReactSelector";
import { FiArrowLeft } from "react-icons/fi";

const FIELD_CLASS =
  "w-full h-11 px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 disabled:bg-slate-50 disabled:cursor-not-allowed";
const LABEL_CLASS = "block text-[11px] font-medium text-slate-400 mb-1.5 uppercase tracking-wide";
const CHECKBOX_CLASS =
  "h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF] focus:ring-[#2B7FFF] disabled:opacity-60";

const AddUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!userId;
  
  const { langMode } = useContext(AppContext);
  
  const [isLoader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [auditMeta, setAuditMeta] = useState({ createdAt: "", updatedAt: "" });
  const baselineRef = useRef(null);
  const [initialValues, setInitialValues] = useState({
    name: "",
    email: "",
    password: "",
    roleId: "",
    active: true,
    agentExtension: "",
    agentSipPassword: "",
    receiveDailyReport: false,
    supervisorUserId: "",
  });

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "44px",
      height: "44px",
      borderRadius: "0.75rem",
      borderColor: state.isFocused ? "#2B7FFF" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(43, 127, 255, 0.15)" : "none",
      "&:hover": { borderColor: state.isFocused ? "#2B7FFF" : "#cbd5e1" },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "44px",
      padding: "0 12px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "44px",
    }),
  };

  const loadRoleOptions = useCallback(async () => {
    try {
      await ensureSuperAdminOrganizationContext();
      const roles = await fetchRolesForUserAssignment();
      const opts = (Array.isArray(roles) ? roles : []).map((r) => ({
        value: r.roleId,
        label: r.displayName || r.roleCode,
        roleCode: r.roleCode,
      }));
      setRoleOptions(opts);
    } catch {
      setRoleOptions([]);
    }
  }, []);

  const loadSupervisorOptions = useCallback(async () => {
    try {
      await ensureSuperAdminOrganizationContext();
      const supervisors = await listCcSupervisorOptions();
      setSupervisorOptions(
        (Array.isArray(supervisors) ? supervisors : [])
          .map((supervisor) => {
            const supervisorId = supervisor.userId ?? supervisor.id;
            return {
              value: supervisorId,
              label: supervisor.name || supervisor.email || String(supervisorId),
            };
          })
          .filter((option) => option.value != null && Number.isFinite(Number(option.value)))
      );
    } catch {
      setSupervisorOptions([]);
    }
  }, []);

  useEffect(() => {
    loadRoleOptions();
    loadSupervisorOptions();
    const onOrgContextChanged = () => {
      loadRoleOptions();
      loadSupervisorOptions();
    };
    window.addEventListener(ORG_CONTEXT_CHANGED_EVENT, onOrgContextChanged);
    return () => {
      window.removeEventListener(ORG_CONTEXT_CHANGED_EVENT, onOrgContextChanged);
    };
  }, [loadRoleOptions, loadSupervisorOptions]);

  const isCallAgentRoleId = useCallback(
    (roleId) => {
      const role = roleOptions.find((item) => Number(item.value) === Number(roleId));
      return String(role?.roleCode || "").toUpperCase() === "CALL_AGENT";
    },
    [roleOptions]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required("Name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        password: isEditMode
          ? Yup.string()
              .nullable()
              .transform((v) => (v === "" || v == null ? undefined : v))
              .test(
                "min-if-set",
                "Password must be at least 8 characters",
                (v) => v == null || String(v).length >= 8
              )
          : Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
        roleId: Yup.mixed()
          .required("Role is required")
          .test("valid-role", "Role is required", (v) => v != null && v !== "" && Number.isFinite(Number(v))),
        agentExtension: Yup.string(),
        agentSipPassword: Yup.string(),
      }),
    [isEditMode]
  );

  useEffect(() => {
    if (isEditMode) {
      setLoader(true);
      getCcWorkspaceUser(userId)
        .then((user) => {
          const next = {
            name: user.name || "",
            email: user.email || "",
            password: "",
            roleId: user.roleId ?? "",
            active: user.active !== false,
            agentExtension: user.agentExtension || "",
            agentSipPassword: "",
            receiveDailyReport: Boolean(user.receiveDailyReport),
            supervisorUserId: user.supervisorUserId ?? "",
          };
          baselineRef.current = { ...next };
          setAuditMeta({
            createdAt: user.createdAt || "",
            updatedAt: user.updatedAt || "",
          });
          setInitialValues(next);
          if (user.roleId != null) {
            setRoleOptions((prev) => {
              const exists = prev.some((o) => Number(o.value) === Number(user.roleId));
              if (exists) return prev;
              return [
                {
                  value: user.roleId,
                  label: user.roleDisplayName || user.roleCode || String(user.roleId),
                  roleCode: user.roleCode || "",
                },
                ...prev,
              ];
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching user:", error);
          const envelope = error?.envelope ?? error?.response?.data;
          toast.error(
            envelope != null ? getCallCenterApiErrorMessage(envelope, "Failed to load user data") : "Failed to load user data"
          );
        })
        .finally(() => setLoader(false));
    }
  }, [userId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      baselineRef.current = null;
      setAuditMeta({ createdAt: "", updatedAt: "" });
    }
  }, [isEditMode]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoader(true);
        const cookieUserId = Cookies.get("userId");
        const isSelf =
          isEditMode && cookieUserId != null && String(cookieUserId) === String(userId);

        if (isEditMode) {
          if (isSelf && values.active === false) {
            toast.error("You cannot deactivate your own account.");
            return;
          }

          const b = baselineRef.current;
          const trim = (s) => String(s ?? "").trim();
          const body = {};
          if (b) {
            if (trim(values.name) !== trim(b.name)) body.name = trim(values.name);
            if (trim(values.email) !== trim(b.email)) body.email = trim(values.email);
            if (Number(values.roleId) !== Number(b.roleId)) body.roleId = Number(values.roleId);
            if (values.active !== b.active) body.active = values.active;
            const ext = trim(values.agentExtension);
            const extB = trim(b.agentExtension);
            if (ext !== extB) body.agentExtension = ext;
            const sipPw = trim(values.agentSipPassword);
            if (sipPw) body.agentSipPassword = sipPw;
            if (!!values.receiveDailyReport !== !!b.receiveDailyReport) {
              body.receiveDailyReport = values.receiveDailyReport;
            }
            if (isCallAgentRoleId(values.roleId)) {
              const nextSupervisor = values.supervisorUserId ? Number(values.supervisorUserId) : null;
              const prevSupervisor = b.supervisorUserId ? Number(b.supervisorUserId) : null;
              if (nextSupervisor !== prevSupervisor) {
                body.updateSupervisor = true;
                body.supervisorUserId = nextSupervisor;
              }
            }
          } else {
            Object.assign(body, {
              name: trim(values.name),
              email: trim(values.email),
              roleId: Number(values.roleId),
              active: values.active,
              agentExtension: trim(values.agentExtension),
              receiveDailyReport: values.receiveDailyReport,
            });
            const sipPwFallback = trim(values.agentSipPassword);
            if (sipPwFallback) body.agentSipPassword = sipPwFallback;
          }

          const pw = values.password?.trim();
          if (Object.keys(body).length > 0) {
            await patchCcWorkspaceUser(userId, body);
          }
          if (pw) {
            await resetCcWorkspaceUserPassword(userId, pw);
          }
          if (Object.keys(body).length === 0 && !pw) {
            toast.info("No changes to save.");
            return;
          }
        } else {
          const createBody = {
            name: values.name.trim(),
            email: values.email.trim(),
            password: values.password,
            roleId: Number(values.roleId),
            active: values.active,
            agentExtension: values.agentExtension?.trim() || undefined,
            agentSipPassword: values.agentSipPassword?.trim() || undefined,
            receiveDailyReport: values.receiveDailyReport,
          };
          if (isCallAgentRoleId(values.roleId) && values.supervisorUserId) {
            createBody.supervisorUserId = Number(values.supervisorUserId);
          }
          await createCcWorkspaceUser(createBody);
        }
        toast.success(isEditMode ? "User updated successfully" : "User created successfully");
        navigate("/all-users");
      } catch (error) {
        const envelope = error?.envelope ?? error?.response?.data;
        toast.error(
          envelope != null ? getCallCenterApiErrorMessage(envelope, "An error occurred. Please try again.") : "An error occurred. Please try again."
        );
      } finally {
        setLoader(false);
      }
    },
  });

  const ErrorMsg = ({ name }) => (
    formik.touched[name] && formik.errors[name] ? (
      <span className="mt-1 text-xs text-red-500">{formik.errors[name]}</span>
    ) : null
  );

  const isSelfEdit =
    isEditMode &&
    Cookies.get("userId") != null &&
    String(Cookies.get("userId")) === String(userId);
  const fieldDisabled = isLoader;
  const showSupervisorField = isCallAgentRoleId(formik.values.roleId);
  const pageTitle = isEditMode ? "Edit User" : translations[langMode].addUser;
  const pageSubtitle = isEditMode
    ? "Update account details, role, and agent phone settings."
    : "Create a new agent or administrator for this workspace.";

  return (
    <div className="min-h-screen bg-[#f9fafb] p-6">
      <div className="mx-auto max-w-4xl">
        <header className="mb-6">
          <button
            type="button"
            onClick={() => navigate("/all-users")}
            className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-[#2B7FFF]"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to users
          </button>
          <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">{pageTitle}</h1>
          <p className="mt-1 text-sm text-[#6b7280]">{pageSubtitle}</p>
        </header>

        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={LABEL_CLASS}>
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...formik.getFieldProps("name")}
                  disabled={fieldDisabled}
                  placeholder="Full name"
                  className={`${FIELD_CLASS} ${
                    formik.touched.name && formik.errors.name ? "border-red-500" : ""
                  }`}
                />
                <ErrorMsg name="name" />
              </div>

              <div>
                <label className={LABEL_CLASS}>
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...formik.getFieldProps("email")}
                  disabled={fieldDisabled}
                  placeholder="name@company.com"
                  className={`${FIELD_CLASS} ${
                    formik.touched.email && formik.errors.email ? "border-red-500" : ""
                  }`}
                />
                <ErrorMsg name="email" />
              </div>

              <div>
                <label className={LABEL_CLASS}>
                  Password {isEditMode ? "(optional)" : <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    {...formik.getFieldProps("password")}
                    disabled={fieldDisabled}
                    placeholder={isEditMode ? "Leave blank to keep current password" : "At least 8 characters"}
                    className={`${FIELD_CLASS} pr-10 ${
                      formik.touched.password && formik.errors.password ? "border-red-500" : ""
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEye size={16} /> : <FaEyeSlash size={16} />}
                  </button>
                </div>
                <ErrorMsg name="password" />
              </div>

              <div>
                <label className={LABEL_CLASS}>
                  Role <span className="text-red-500">*</span>
                </label>
                <ReactSelector
                  options={roleOptions}
                  styles={customSelectStyles}
                  isDisabled={fieldDisabled}
                  placeholder="Select role"
                  value={roleOptions.find((item) => Number(item.value) === Number(formik.values.roleId))}
                  onChange={(e) => formik.setFieldValue("roleId", e?.value ?? "")}
                />
                <ErrorMsg name="roleId" />
              </div>

              <div>
                <label className={LABEL_CLASS}>Agent extension</label>
                <input
                  type="text"
                  {...formik.getFieldProps("agentExtension")}
                  disabled={fieldDisabled}
                  placeholder="PBX extension"
                  className={`${FIELD_CLASS} ${
                    formik.touched.agentExtension && formik.errors.agentExtension ? "border-red-500" : ""
                  }`}
                />
                <ErrorMsg name="agentExtension" />
              </div>

              <div>
                <label className={LABEL_CLASS}>Agent SIP password</label>
                <input
                  type="password"
                  {...formik.getFieldProps("agentSipPassword")}
                  disabled={fieldDisabled}
                  placeholder={
                    isEditMode
                      ? "Leave blank to keep current SIP password"
                      : "UCM extension WebRTC password"
                  }
                  className={FIELD_CLASS}
                />
                <ErrorMsg name="agentSipPassword" />
              </div>

              {showSupervisorField && (
                <div>
                  <label className={LABEL_CLASS}>Supervisor</label>
                  <ReactSelector
                    options={supervisorOptions}
                    styles={customSelectStyles}
                    isDisabled={fieldDisabled}
                    isClearable
                    placeholder="Select supervisor"
                    value={
                      supervisorOptions.find(
                        (item) => Number(item.value) === Number(formik.values.supervisorUserId)
                      ) || null
                    }
                    onChange={(option) =>
                      formik.setFieldValue("supervisorUserId", option?.value ?? "")
                    }
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    Used for SLA alert escalation to this agent&apos;s supervisor.
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
                <label className="inline-flex cursor-pointer items-center gap-2.5 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    id="active"
                    className={CHECKBOX_CLASS}
                    disabled={fieldDisabled}
                    checked={formik.values.active}
                    onChange={(e) => {
                      if (isSelfEdit && !e.target.checked) {
                        toast.error("You cannot deactivate your own account.");
                        return;
                      }
                      formik.setFieldValue("active", e.target.checked);
                    }}
                  />
                  Active status
                </label>

                <label className="inline-flex cursor-pointer items-center gap-2.5 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    id="report"
                    className={CHECKBOX_CLASS}
                    disabled={fieldDisabled}
                    checked={formik.values.receiveDailyReport}
                    onChange={(e) => formik.setFieldValue("receiveDailyReport", e.target.checked)}
                  />
                  Receive daily report
                </label>
              </div>
            </div>

            {isEditMode && (auditMeta.createdAt || auditMeta.updatedAt) && (
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400">
                {auditMeta.createdAt ? (
                  <span>Created: {new Date(auditMeta.createdAt).toLocaleString()}</span>
                ) : null}
                {auditMeta.updatedAt ? (
                  <span>Last updated: {new Date(auditMeta.updatedAt).toLocaleString()}</span>
                ) : null}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-5">
              <button
                type="button"
                onClick={() => navigate("/all-users")}
                className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoader}
                className="h-11 rounded-xl bg-[#2B7FFF] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1d6ef2] disabled:opacity-60"
              >
                {isLoader ? "Processing..." : isEditMode ? "Update User" : "Save User"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUser;